import { useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import {
  NumberInputProps,
  SelectOption,
  ValidatedFieldProps,
  ValidationItems,
  Widget,
} from '@kanda-libs/ks-component-ts';

import get from 'lodash.get';
import { ApplyFormFieldValues } from 'pages/Apply/context';

import {
  BASE_WIDGET,
  ERROR_PROPS,
  MONTHS_AT_ADDRESS_BASE_FIELDNAMES,
  YEARS_AT_ADDRESS_INDEXES,
} from './constants';
import {
  checkBeforePrevAddress,
  checkInPast,
  generateYearsOptions,
  getTime,
} from './helpers';

import { AddressFieldType } from '..';

export interface YearsAtAddressFieldsHook {
  monthsName: string;
  yearsName: string;
  yearsOptions: SelectOption[];
  error?: string;
  validation: ValidationItems;
  baseWidget: (props: ValidatedFieldProps<NumberInputProps>) => JSX.Element;
}

export default function useYearsAtAddressFields(
  addressType: AddressFieldType,
): YearsAtAddressFieldsHook {
  const { getValues, setValue, formState, trigger } = useFormContext();

  const rootName = useMemo(
    () => `address.${YEARS_AT_ADDRESS_INDEXES[addressType]}`,
    [addressType],
  );

  const monthsName = useMemo(() => `${rootName}.month_moved_in`, [rootName]);

  const yearsName = useMemo(() => `${rootName}.year_moved_in`, [rootName]);

  const baseFieldName = useMemo(
    () => MONTHS_AT_ADDRESS_BASE_FIELDNAMES[addressType],
    [addressType],
  );

  const baseWidget = useMemo(() => BASE_WIDGET[addressType], [addressType]);

  const [years, months] = useWatch({
    name: [yearsName, monthsName],
  });

  const yearsOptions = useMemo(() => generateYearsOptions(), []);

  const baseValidation = useMemo(() => {
    if (addressType === 'current')
      return Widget.CreditCustomerDetailsCurrentAddressMonthsAtAddressValidation;
    if (addressType === 'previous1')
      return Widget.CreditCustomerDetailsPreviousAddressOneMonthsAtAddressValidation;
    return Widget.CreditCustomerDetailsPreviousAddressTwoMonthsAtAddressValidation;
  }, [addressType]);

  const validation = useMemo(
    () => ({
      ...baseValidation,
      required: {
        value: true,
        message: 'You must provide the date you moved into this address',
      },
      validate: {
        value: {
          inPast: () =>
            checkInPast(getValues() as ApplyFormFieldValues, addressType),
          beforePrevAddress: () =>
            checkBeforePrevAddress(
              getValues() as ApplyFormFieldValues,
              addressType,
            ),
        },
        message: {
          inPast: 'The time provided must be in the past',
          beforePrevAddress:
            'The date you moved in must be before the previous address',
        },
      },
    }),
    [baseValidation, addressType, getValues],
  );

  const error = useMemo(() => {
    if (years && months) {
      const time = getTime(years, months);
      if (time < 0) return 'The time provided must be in the past';
    }
    const heldError = get(formState?.errors, baseFieldName);
    if (!heldError) return undefined;
    if (heldError?.type === 'required') return ERROR_PROPS.label.required;
    if (heldError?.type === 'inPast') return ERROR_PROPS.label.inPast;
    if (heldError?.type === 'beforePrevAddress')
      return ERROR_PROPS.label.beforePrevAddress;
    return 'There is an error with this input';
  }, [formState, baseFieldName, years, months]);

  // Effect unsets value if certain conditions met
  useEffect(() => {
    if (!years || !months) {
      setValue(baseFieldName, undefined);
      return;
    }
    const time = getTime(years, months);
    if (time < 0) {
      setValue(baseFieldName, undefined);
      return;
    }
    setValue(baseFieldName, time);
    trigger(baseFieldName);
  }, [years, months, setValue, baseFieldName, trigger]);

  // Effect unsets value if error is present
  useEffect(() => {
    if (!error) return;
    setValue(baseFieldName, undefined);
  }, [error, baseFieldName, setValue]);

  return {
    monthsName,
    yearsName,
    yearsOptions,
    error,
    validation,
    baseWidget,
  };
}
